import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_TIME_FORMAT,
  Timestamp,
  Timezone,
  TypeGuard,
  toTimestamp,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { STERILISATION_MACHINE_DESTINATION_ENTITY } from '../../../destination/entities/sterilisation-machine';
import { STERILISATION_MACHINE_RESOURCE_TYPE } from '../../../mappings/sterilisation-machine';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';

export const STERILISATION_MACHINE_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Sterilisation Machine List',
    description: '',
    idPrefix: STERILISATION_MACHINE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface ID4WSterilisationMachine {
  id: number;
  isActive: boolean;
  name: string;
  make?: string;
  model?: string;
  serial?: number;
  notes?: string;
  practiceId: number;
  createdAt: string;
}

export interface ID4WSterilisationMachineTranslations {
  createdAt: Timestamp;
}

export function isD4WSterilisationMachine(
  item: unknown,
  logError: boolean
): item is ID4WSterilisationMachine {
  return TypeGuard.interface<ID4WSterilisationMachine>(
    {
      id: isNumber,
      isActive: isBoolean,
      name: isString,
      make: TypeGuard.nilOr(isString),
      model: TypeGuard.nilOr(isString),
      serial: TypeGuard.nilOr(isNumber),
      notes: TypeGuard.nilOr(isString),
      practiceId: isNumber,
      createdAt: isString,
    },
    logError
  )(item);
}

const STERILISATION_SOURCE_QUERY = `
SELECT
  id_sterilizer AS id,
  convert_to_boolean(is_active) AS is_active,
  name,
  NULLIF(make, '') AS make,
  NULLIF(model, '') AS model,
  NULLIF(convert_to_integer(serial), 0) AS serial,
  NULLIF(notes, '') AS notes,
  id_practice AS practice_id,
  sync_date AS created_at
FROM ast_sterilizers
WHERE id_sterilizer IS NOT NULL`;

export type D4WSterilisationMachine = ISourceEntityTypes<
  ID4WSterilisationMachine,
  ID4WSterilisationMachineTranslations
>;

export class SterilisationMachineSourceEntity extends BaseSourceEntity<D4WSterilisationMachine> {
  sourceEntity = STERILISATION_MACHINE_SOURCE_ENTITY;
  entityResourceType = STERILISATION_MACHINE_RESOURCE_TYPE;
  sourceQuery = STERILISATION_SOURCE_QUERY;
  migrationDestinations = [
    STERILISATION_MACHINE_DESTINATION_ENTITY.metadata.key,
  ];
  verifySourceFn = isD4WSterilisationMachine;

  override translate(
    sterilisationMachine: ID4WSterilisationMachine,
    timezone: Timezone
  ): ID4WSterilisationMachineTranslations {
    return {
      createdAt: toTimestamp(
        moment.tz(
          sterilisationMachine.createdAt,
          ISO_DATE_TIME_FORMAT,
          timezone
        )
      ),
    };
  }

  getSourceRecordId(data: ID4WSterilisationMachine): number {
    return data.id;
  }

  getSourceLabel(data: ID4WSterilisationMachine): string {
    return data.name;
  }
}
